import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { siteConfig } from '../../config/site';

const Wrap = styled.section`
  background:
    radial-gradient(1000px 500px at 50% -20%, rgba(0,153,0,0.20), transparent 60%),
    linear-gradient(180deg, var(--background-dark), var(--background-light));
  padding: 5rem 1rem 4rem;
  position: relative;
  overflow: hidden;
`;

const Inner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

const Copy = styled.div` color: var(--text-color); `;

const Ribbon = styled.span`
  display: inline-flex; align-items: center; gap: 0.5rem;
  padding: 0.35rem 0.9rem;
  border-radius: 6px;
  background: rgba(0, 153, 0, 0.15);
  color: var(--primary-color);
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin-bottom: 1.25rem;
`;

const Title = styled.h1`
  font-size: clamp(2.5rem, 5.5vw, 4.25rem);
  line-height: 1.02;
  margin: 0 0 1.25rem;
  color: var(--text-color);
  span { color: var(--primary-color); }
`;

const Lede = styled.p`
  font-size: 1.15rem;
  line-height: 1.7;
  color: var(--text-secondary);
  margin: 0 0 2rem;
  max-width: 560px;
  @media (max-width: 900px) { margin-left: auto; margin-right: auto; }
`;

const Actions = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 2rem;
  @media (max-width: 900px) { justify-content: center; }
`;

const PrimaryLink = styled(Link)`
  background: var(--primary-color);
  color: var(--text-color);
  padding: 1rem 2rem;
  border-radius: var(--border-radius);
  font-weight: 800;
  text-decoration: none;
  transition: transform 0.2s ease;
  &:hover { transform: translateY(-2px); }
`;

const GhostLink = styled.a`
  border: 1px solid var(--primary-color);
  color: var(--primary-color);
  padding: 1rem 2rem;
  border-radius: var(--border-radius);
  font-weight: 700;
  text-decoration: none;
  transition: background 0.2s ease;
  &:hover { background: rgba(0, 153, 0, 0.1); }
`;

const Meta = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  color: var(--text-secondary);
  font-size: 0.9rem;
  @media (max-width: 900px) { justify-content: center; }

  .item {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
  }
  .dot { width: 6px; height: 6px; border-radius: 50%; background: var(--primary-color); }
`;

const Right = styled.div` position: relative; `;

const ProductCard = styled.div`
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(0,153,0,0.25);
  border-radius: 1.75rem;
  padding: 2rem 2rem 1.75rem;
  box-shadow: 0 30px 80px -30px rgba(0,0,0,0.6);
  position: relative;
`;

const Badge = styled.span`
  position: absolute;
  top: -12px; right: 20px;
  background: var(--primary-color);
  color: var(--text-color);
  padding: 0.35rem 0.8rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const ProductImage = styled.figure`
  margin: 0 0 1rem;
  aspect-ratio: 4/3;
  background: rgba(0,0,0,0.35);
  border-radius: 1rem;
  border: 1px solid rgba(0,153,0,0.2);
  display: grid;
  place-items: center;
  overflow: hidden;
  padding: 1.5rem;
  img { max-width: 80%; max-height: 80%; object-fit: contain; }
`;

const Row = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.75rem;

  h3 { margin: 0; color: var(--text-color); font-size: 1.2rem; }
  .price { color: var(--primary-color); font-weight: 800; font-size: 1.4rem; }
`;

const Bullets = styled.ul`
  list-style: none; padding: 0; margin: 0 0 1.25rem;
  color: var(--text-secondary); font-size: 0.9rem;
  li { padding: 0.25rem 0; }
  li::before { content: '✓ '; color: var(--primary-color); font-weight: 800; margin-right: 0.4rem; }
`;

const BuyBtn = styled.a`
  display: block; text-align: center;
  background: var(--primary-color); color: var(--text-color);
  padding: 0.85rem 1rem;
  border-radius: 12px;
  font-weight: 800;
  text-decoration: none;
  transition: transform 0.2s ease;
  &:hover { transform: translateY(-2px); }
`;

const StoreHero: React.FC = () => (
  <Wrap>
    <Inner>
      <Copy>
        <Ribbon>🛒 In stock · Instant email delivery</Ribbon>
        <Title>
          Buy <span>flash USDT software</span>. Straight to your inbox in minutes.
        </Title>
        <Lede>
          {siteConfig.brand.name} is the online store for licensed flash USDT software.
          Pay in BTC or USDT, receive the license key by email within minutes, start flashing
          on TRC-20, ERC-20 or BEP-20 the same day.
        </Lede>
        <Actions>
          <PrimaryLink to="/pricing">Shop pricing plans</PrimaryLink>
          <GhostLink href={`https://wa.me/${siteConfig.contact.whatsapp}`} target="_blank" rel="noopener noreferrer">
            Chat on WhatsApp
          </GhostLink>
        </Actions>
        <Meta>
          <span className="item"><span className="dot" /> BTC &amp; USDT accepted</span>
          <span className="item"><span className="dot" /> Instant delivery</span>
          <span className="item"><span className="dot" /> 24/7 support</span>
        </Meta>
      </Copy>

      <Right>
        <ProductCard>
          <Badge>Best seller</Badge>
          <ProductImage>
            <img src="/img/product-flash-1.png" alt="USDT Flasher Store — flash USDT software product preview" />
          </ProductImage>
          <Row>
            <h3>Pro License · Lifetime</h3>
            <span className="price">$5,000</span>
          </Row>
          <Bullets>
            <li>$10,000,000 daily flash cap</li>
            <li>Lifetime access, all networks</li>
            <li>365-day flash validity</li>
            <li>White-glove 24/7 support</li>
          </Bullets>
          <BuyBtn href={`https://wa.me/${siteConfig.contact.whatsapp}?text=${encodeURIComponent('I want to buy the Pro License ($5,000) from USDT Flasher Store')}`} target="_blank" rel="noopener noreferrer">
            Buy on WhatsApp →
          </BuyBtn>
        </ProductCard>
      </Right>
    </Inner>
  </Wrap>
);

export default StoreHero;
